/**
 * Transaction Tags Service
 * 
 * Service for managing transaction tag assignments
 * Handles assigning and removing tags from transactions
 */

import { supabase } from '../lib/supabase';
import type {
  ServiceResponse,
  DeleteResponse
} from '../types/transaction.types';

class TransactionTagsService {
  /**
   * Assign tags to transaction
   */
  async assignTagsToTransaction(
    transactionId: string,
    userId: string,
    tagIds: string[]
  ): Promise<ServiceResponse<{ transactionId: string; tagIds: string[] }>> {
    try {
      // Verify transaction belongs to user
      const { data: transaction, error: transactionError } = await supabase
        .from('transactions')
        .select('id')
        .eq('id', transactionId)
        .eq('user_id', userId)
        .single();

      if (transactionError || !transaction) {
        return {
          error: {
            code: 'NOT_FOUND',
            message: 'Transaction not found'
          }
        };
      }

      // Verify all tags exist and belong to user
      const { data: tags, error: tagsError } = await supabase
        .from('transaction_tags')
        .select('id')
        .in('id', tagIds)
        .eq('user_id', userId);

      if (tagsError || !tags || tags.length !== tagIds.length) {
        return {
          error: {
            code: 'NOT_FOUND',
            message: 'One or more tags not found'
          }
        };
      }

      // Remove duplicates
      const uniqueTagIds = [...new Set(tagIds)];

      // Create tag assignments
      const assignments = uniqueTagIds.map(tagId => ({
        transaction_id: transactionId,
        tag_id: tagId
      }));

      const { error: assignError } = await supabase
        .from('transaction_tag_assignments')
        .upsert(assignments, { onConflict: 'transaction_id,tag_id' });

      if (assignError) {
        return {
          error: {
            code: 'INSERT_ERROR',
            message: `Failed to assign tags: ${assignError.message}`
          }
        };
      }

      return {
        data: {
          transactionId,
          tagIds: uniqueTagIds
        }
      };
    } catch (error: any) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error.message || 'An unexpected error occurred'
        }
      };
    }
  }

  /**
   * Get tags for a transaction
   */
  async getTagsForTransaction(
    transactionId: string,
    userId: string
  ): Promise<ServiceResponse<Array<{ id: string; name: string; color: string }>>> {
    try {
      // Verify transaction belongs to user
      const { data: transaction, error: transactionError } = await supabase
        .from('transactions')
        .select('id')
        .eq('id', transactionId)
        .eq('user_id', userId)
        .single();

      if (transactionError || !transaction) {
        return {
          error: {
            code: 'NOT_FOUND',
            message: 'Transaction not found'
          }
        };
      }

      // Get tag assignments for this transaction
      const { data: assignments, error: assignmentsError } = await supabase
        .from('transaction_tag_assignments')
        .select('tag_id, transaction_tags(id, name, color)')
        .eq('transaction_id', transactionId);

      if (assignmentsError) {
        return {
          error: {
            code: 'QUERY_ERROR',
            message: `Failed to fetch tags: ${assignmentsError.message}`
          }
        };
      }

      // Extract tags from assignments
      const tags = (assignments || [])
        .map((assignment: any) => {
          if (assignment.transaction_tags) {
            return {
              id: assignment.transaction_tags.id,
              name: assignment.transaction_tags.name,
              color: assignment.transaction_tags.color || '#3B82F6'
            };
          }
          return null;
        })
        .filter((tag): tag is { id: string; name: string; color: string } => tag !== null);

      return {
        data: tags
      };
    } catch (error: any) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error.message || 'An unexpected error occurred'
        }
      };
    }
  }

  /**
   * Remove tag from transaction
   */
  async removeTagFromTransaction(
    transactionId: string,
    userId: string,
    tagId: string
  ): Promise<DeleteResponse> {
    try {
      // Verify transaction belongs to user
      const { data: transaction, error: transactionError } = await supabase
        .from('transactions')
        .select('id')
        .eq('id', transactionId)
        .eq('user_id', userId)
        .single();

      if (transactionError || !transaction) {
        return {
          success: false,
          error: {
            code: 'NOT_FOUND',
            message: 'Transaction not found'
          }
        };
      }

      // Verify tag exists
      const { data: tag, error: tagError } = await supabase
        .from('transaction_tags')
        .select('id')
        .eq('id', tagId)
        .single();

      if (tagError || !tag) {
        return {
          success: false,
          error: {
            code: 'NOT_FOUND',
            message: 'Tag not found'
          }
        };
      }

      // Verify tag assignment exists
      const { data: assignment, error: assignmentError } = await supabase
        .from('transaction_tag_assignments')
        .select('id')
        .eq('transaction_id', transactionId)
        .eq('tag_id', tagId)
        .single();

      if (assignmentError || !assignment) {
        return {
          success: false,
          error: {
            code: 'NOT_FOUND',
            message: 'Tag assignment not found'
          }
        };
      }

      // Delete tag assignment
      const { error: deleteError } = await supabase
        .from('transaction_tag_assignments')
        .delete()
        .eq('transaction_id', transactionId)
        .eq('tag_id', tagId);

      if (deleteError) {
        return {
          success: false,
          error: {
            code: 'DELETE_ERROR',
            message: `Failed to remove tag: ${deleteError.message}`
          }
        };
      }

      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: {
          code: 'UNKNOWN_ERROR',
          message: error.message || 'An unexpected error occurred'
        }
      };
    }
  }
}

// Export singleton instance
export const transactionTagsService = new TransactionTagsService();

